import React from "react";
import { motion } from "framer-motion";

const DEFAULT_GRADIENT =
  "conic-gradient(from 0deg, #1a3d7c 0%, #2a5ba8 18%, #30cd73 36%, #22c55e 50%, #30cd73 64%, #2a5ba8 82%, #1a3d7c 100%)";

interface GradientBorderCardProps {
  active: boolean;
  borderWidth?: number;
  duration?: number;
  borderRadius?: number;
  gradient?: string;
  glowShadow?: string;
  className?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
}

const GradientBorderCard: React.FC<GradientBorderCardProps> = ({
  active,
  borderWidth = 3,
  duration = 4,
  borderRadius = 22,
  gradient = DEFAULT_GRADIENT,
  glowShadow,
  className,
  style,
  children,
}) => {
  if (!active) {
    return (
      <div className={className} style={style}>
        {children}
      </div>
    );
  }

  return (
    <div
      className={className}
      style={{
        ...style,
        position: "relative",
        borderRadius: `${borderRadius}px`,
        padding: `${borderWidth}px`,
        overflow: "hidden",
        transform: "translateZ(0)",
        ...(glowShadow && { boxShadow: glowShadow }),
      }}
    >
      {/*
        Two-element centering trick:
        - Outer div: pure CSS centering via translate(-50%, -50%) — never animated
        - Inner motion.div: rotation only — no positioning transforms
        Keeping them separate prevents framer-motion's transform stack from
        interfering with the CSS offset, which caused the border to only appear
        at the top of tall cards when both were on the same element.
        Width = 800% of wrapper width (width-relative %, always resolves correctly
        unlike height%) with aspect-ratio 1 ensures the square covers the full
        card diagonal at every rotation angle, even for extremely tall plans.
      */}
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          width: "800%", // Covers heights up to ~7.9x width
          aspectRatio: "1",
          transform: "translate(-50%, -50%)",
          pointerEvents: "none",
        }}
      >
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration, repeat: Infinity, ease: "linear" }}
          style={{
            width: "100%",
            height: "100%",
            background: gradient,
          }}
        />
      </div>

      {/* Content layer sits above the gradient */}
      <div style={{ position: "relative", zIndex: 1 }}>
        {children}
      </div>
    </div>
  );
};

export default GradientBorderCard;
